// frontend/src/pages/Login.jsx
// Página de inicio de sesión con validación y manejo de errores
import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isPlatformStandaloneMode } from '../utils/runtimeMode';
import { inicioDePortal, rutaPermitidaEnPortal } from '../utils/portales';

// ─── Animaciones ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Componentes Styled ───────────────────────────────────────────────────────

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.lg};
  }
`;

const LoginCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.xxl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 100%;
  max-width: 420px;
  animation: ${fadeIn} 0.4s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-width: 340px;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.lg};
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const AppTitle = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xxl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0 0 ${({ theme }) => theme.spacing.xs};
`;

const AppSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0;
`;

const TenantBadge = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.info}18;
  border: 1px solid ${({ theme }) => theme.colors.info};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const StyledInput = styled.input`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border: 1px solid ${({ theme, $error }) => $error ? theme.colors.danger : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
  }
`;

const LoginButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.lg}`};
  background: ${({ theme, disabled }) => disabled ? theme.colors.textMuted : theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s, transform 0.1s;
  margin-top: ${({ theme }) => theme.spacing.sm};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryHover};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  background: ${({ theme }) => theme.colors.danger}22;
  border: 1px solid ${({ theme }) => theme.colors.danger};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-align: center;
`;

const SecurityWarningMessage = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  background: ${({ theme }) => theme.colors.warning}22;
  border: 1px solid ${({ theme }) => theme.colors.warning};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: 1.4;
`;

const FieldError = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.danger};
`;

// ─── Componente principal ─────────────────────────────────────────────────────

const ForgotPasswordNote = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  background: ${({ theme }) => theme.colors.info}15;
  border: 1px solid ${({ theme }) => theme.colors.info}55;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: 1.5;
`;

const ForgotLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-decoration: underline;
  display: block;
  width: 100%;
  text-align: center;
  font-family: inherit;

  &:hover { opacity: 0.8; }
`;

const Login = () => {
  const navigate = useNavigate();
  const platformStandalone = isPlatformStandaloneMode();
  const {
    login,
    loading,
    tenantSlug,
    tenantNombre,
    securityWarning,
    clearSecurityWarning,
  } = useAuth();

  const location = useLocation();

  // A dónde volver después de entrar. Dos vías, y las dos hacen falta:
  //
  // - `?next=` en la URL, para enlaces armados desde afuera de la app.
  // - `state.from`, que es lo que manda `ProtectedRoute` cuando intercepta una
  //   ruta protegida. Sin esto, quien abría la app instalada —que arranca en
  //   /app— se logueaba y aterrizaba en el dashboard de escritorio, teniendo
  //   que volver a buscar a mano dónde estaba yendo.
  const desdeState = location.state?.from?.pathname;
  const nextParam = new URLSearchParams(window.location.search).get('next') || desdeState || null;

  const [formData, setFormData] = useState({ username: '', password: '' });
  const [errores, setErrores] = useState({});
  const [errorGeneral, setErrorGeneral] = useState('');
  const [mostrarRecuperacion, setMostrarRecuperacion] = useState(false);

  // Actualizar campo del formulario y limpiar errores al escribir
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errores[name]) setErrores((prev) => ({ ...prev, [name]: '' }));
    setErrorGeneral('');
    if (securityWarning) {
      clearSecurityWarning();
    }
  };

  // Validaciones del lado cliente antes de llamar al servidor
  const validar = () => {
    const nuevosErrores = {};
    if (!formData.username.trim()) nuevosErrores.username = 'El usuario es obligatorio';
    if (!formData.password) nuevosErrores.password = 'La contraseña es obligatoria';
    return nuevosErrores;
  };

  // Enviar el formulario de login
  const handleSubmit = async (e) => {
    e.preventDefault();

    const erroresValidacion = validar();
    if (Object.keys(erroresValidacion).length > 0) {
      setErrores(erroresValidacion);
      return;
    }

    const resultado = await login({
      username: formData.username.trim(),
      password: formData.password,
    });

    if (resultado.success) {
      const rol = resultado.user?.rol;
      const inicio = inicioDePortal(rol);
      // El `next` solo vale si el rol puede visitarlo: para un rol de portal,
      // un destino de afuera terminaría rebotando igual contra ProtectedRoute.
      const destino = nextParam && rutaPermitidaEnPortal(rol, nextParam)
        ? nextParam
        : inicio;
      navigate(destino, { replace: true });
    } else {
      setErrorGeneral(resultado.error || 'Credenciales incorrectas. Verifique usuario y contraseña.');
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        {/* Título de la aplicación */}
        <Logo>
          <AppTitle>FC Cloud</AppTitle>
          <AppSubtitle>
            {platformStandalone
              ? 'Plataforma central de administracion de tenants'
              : 'Gestión Integral de Mantenimiento en la Nube'}
          </AppSubtitle>
          <TenantBadge>
            {platformStandalone
              ? 'Plataforma de administración'
              : tenantNombre
                ? `Ingreso a ${tenantNombre}`
                : tenantSlug
                  ? `Ingreso a ${tenantSlug}`
                  : `Ingreso al sistema`}
          </TenantBadge>
        </Logo>

        {/* Error general retornado por el servidor */}
        {securityWarning && (
          <SecurityWarningMessage>
            <strong>Sesión cruzada detectada:</strong> por seguridad se cerró una sesión guardada de otro host.
            {securityWarning?.storedContext?.frontendHost && securityWarning?.currentContext?.frontendHost && (
              <div>
                Host anterior: <strong>{securityWarning.storedContext.frontendHost}</strong> | Host actual: <strong>{securityWarning.currentContext.frontendHost}</strong>
              </div>
            )}
          </SecurityWarningMessage>
        )}
        {errorGeneral && <ErrorMessage>{errorGeneral}</ErrorMessage>}

        <Form onSubmit={handleSubmit} noValidate>
          {/* Campo usuario */}
          <FormGroup>
            <Label htmlFor="username">Usuario</Label>
            <StyledInput
              id="username"
              name="username"
              type="text"
              value={formData.username}
              onChange={handleChange}
              placeholder="Ingrese su usuario"
              $error={!!errores.username}
              autoComplete="username"
              autoFocus
            />
            {errores.username && <FieldError>{errores.username}</FieldError>}
          </FormGroup>

          {/* Campo contraseña */}
          <FormGroup>
            <Label htmlFor="password">Contraseña</Label>
            <StyledInput
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Ingrese su contraseña"
              $error={!!errores.password}
              autoComplete="current-password"
            />
            {errores.password && <FieldError>{errores.password}</FieldError>}
          </FormGroup>

          {/* Botón ingresar */}
          <LoginButton type="submit" disabled={loading}>
            {loading ? 'Ingresando...' : 'Ingresar'}
          </LoginButton>
        </Form>

        <ForgotLink type="button" onClick={() => setMostrarRecuperacion(v => !v)}>
          ¿Olvidaste tu contraseña?
        </ForgotLink>

        {mostrarRecuperacion && (
          <ForgotPasswordNote>
            <strong>Recuperación de contraseña</strong><br />
            Para restablecer tu contraseña, comunicarte con el administrador del sistema. Él puede asignarte una contraseña temporal desde el panel de configuración de usuarios.
          </ForgotPasswordNote>
        )}
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
